import { CSSProperties, ReactElement } from 'react'

import { ConditionalWrapper } from 'components/ConditionalWrapper'
import { Flex, Icon } from 'components/core'
import { Popover } from 'components/Popover'
import { DELTA_COLORS } from 'const/colors'
import { theme } from 'styles/theme'

interface Props {
    value: number
    formatter: (value: number) => ReactElement | string | number
    fontSize?: number
    inverse?: boolean
    neutral?: boolean
    iconOnly?: boolean
    className?: string
    style?: CSSProperties
    affix?: 'prefix' | 'suffix'
}

const ChangeDelta = ({
    value,
    formatter,
    inverse = false,
    neutral = false,
    iconOnly = false,
    className,
    style,
    affix = 'prefix',
}: Props): ReactElement => {
    const colors = inverse
        ? [DELTA_COLORS.RED, DELTA_COLORS.GREEN, DELTA_COLORS.GRAY]
        : [DELTA_COLORS.GREEN, DELTA_COLORS.RED, DELTA_COLORS.GRAY]

    const bgColors = inverse
        ? [DELTA_COLORS.RED_BG, DELTA_COLORS.GREEN_BG, DELTA_COLORS.GRAY_BG]
        : [DELTA_COLORS.GREEN_BG, DELTA_COLORS.RED_BG, DELTA_COLORS.GRAY_BG]

    let affixContent
    let color = colors[2]
    let bgColor = bgColors[2]
    if (value > 0) {
        color = colors[0]
        affixContent = <Icon name="ARROW_RIGHT" size={13} rotate={-45} />
        bgColor = bgColors[0]
    } else if (value < 0) {
        color = colors[1]
        affixContent = <Icon name="ARROW_RIGHT" size={13} rotate={45} />
        bgColor = bgColors[1]
    } else if (value === 0) {
        color = colors[2]
        bgColor = bgColors[2]
    }

    if (neutral) {
        color = colors[2]
        bgColor = bgColors[2]
    }

    return (
        <ConditionalWrapper
            condition={iconOnly}
            wrapper={(children) => (
                <Popover
                    content={formatter(Math.abs(value))}
                    trigger="hover"
                    placement="left"
                    styles={{
                        root: {
                            color,
                        },
                    }}
                >
                    {children}
                </Popover>
            )}
        >
            <Flex
                inline
                justifyContent="end"
                gap={iconOnly ? 0 : 4}
                className={className}
                style={{
                    background: bgColor,
                    padding: iconOnly ? '2px 5px' : '2px 8px 2px 5px',
                    borderRadius: 8,
                    color,
                    fontSize: theme.fontSize.bodySm,
                    ...style,
                }}
            >
                {affix === 'prefix' && affixContent}
                {!iconOnly && formatter(Math.abs(value))}
                {affix === 'suffix' && affixContent}
            </Flex>
        </ConditionalWrapper>
    )
}

export default ChangeDelta
